import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  IconButton,
  Tooltip,
  Fade,
  Zoom
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  ArrowForward,
  Info,
  Analytics,
  SmartToy,
  Chat
} from '@mui/icons-material';
import { KPIIndicator } from '../types';

interface KPICardProps {
  kpi: KPIIndicator;
  onClick: (kpi: KPIIndicator) => void;
}

const getTrendIcon = (trend?: 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return <TrendingUp color="success" />;
    case 'down':
      return <TrendingDown color="error" />;
    case 'stable':
      return <TrendingFlat color="action" />;
    default:
      return null;
  }
};

const getTrendColor = (trend?: 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return 'success';
    case 'down':
      return 'error';
    case 'stable':
      return 'default';
    default:
      return 'default';
  }
};

const KPICard: React.FC<KPICardProps> = ({ kpi, onClick }) => {
  // 判断是否可点击：有图表或者是AI分析相关功能
  const isClickable = kpi.hasChart || kpi.category === 'ai-analytics';
  
  return (
    <Zoom in timeout={300}>
      <Card
        sx={{
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: isClickable ? 'pointer' : 'default',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: isClickable 
              ? 'linear-gradient(90deg, #3b82f6, #60a5fa, #93c5fd)'
              : 'linear-gradient(90deg, #9ca3af, #d1d5db, #e5e7eb)',
          },
          '&:hover': isClickable ? {
            transform: 'translateX(4px) translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            '& .arrow-icon': {
              opacity: 1,
              transform: 'translateX(4px)',
            },
            '& .info-icon': {
              opacity: 1,
            },
          } : {},
        }}
        onClick={isClickable ? () => onClick(kpi) : undefined}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                {kpi.title}
              </Typography>
              {isClickable && (
                <Tooltip title="点击查看详细分析" arrow>
                  <Info 
                    className="info-icon"
                    sx={{ 
                      opacity: 0, 
                      transition: 'opacity 0.3s ease',
                      color: 'primary.main',
                      fontSize: '1rem'
                    }} 
                  />
                </Tooltip>
              )}
            </Box>
            {isClickable && (
              <IconButton
                size="small"
                className="arrow-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  onClick(kpi);
                }}
                sx={{
                  opacity: 0,
                  transition: 'all 0.3s ease',
                  color: 'primary.main',
                  cursor: 'pointer',
                }}
              >
                <ArrowForward />
              </IconButton>
            )}
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, lineHeight: 1.5 }}
          >
            {kpi.description}
          </Typography>

          <Grid container spacing={2}>
            {kpi.data.map((item, index) => (
              <Grid item xs={6} key={index}>
                <Fade in timeout={500 + index * 100}>
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.5,
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}
                    >
                      {item.label}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600 }}
                        color={getTrendColor(item.trend)}
                      >
                        {item.value}
                      </Typography>
                      {getTrendIcon(item.trend)}
                    </Box>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>

          {kpi.hasChart && (
            <Fade in timeout={800}>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Chip
                  label="查看图表"
                  size="small"
                  color="primary"
                  variant="outlined"
                  icon={<Analytics />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick(kpi);
                  }}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: 'white',
                    },
                  }}
                />
              </Box>
            </Fade>
          )}

          {/* AI运营分析特殊按钮 */}
          {kpi.id === 'ai-operations-analysis' && (
            <Fade in timeout={800}>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Chip
                  label="启动AI自动运营分析"
                  size="medium"
                  color="success"
                  variant="filled"
                  icon={<SmartToy />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick(kpi);
                  }}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    padding: '8px 16px',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(5, 150, 105, 0.4)',
                    },
                    '& .MuiChip-icon': {
                      color: 'white',
                      fontSize: '1.2rem',
                    },
                  }}
                />
              </Box>
            </Fade>
          )}

          {/* AI智能对话特殊按钮 */}
          {kpi.id === 'ai-chat-assistant' && (
            <Fade in timeout={800}>
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <Chip
                  label="启动AI智能对话小助手"
                  size="medium"
                  color="primary"
                  variant="filled"
                  icon={<Chat />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick(kpi);
                  }}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    padding: '8px 16px',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                    },
                    '& .MuiChip-icon': {
                      color: 'white',
                      fontSize: '1.2rem',
                    },
                  }}
                />
              </Box>
            </Fade>
          )}
        </CardContent>
      </Card>
    </Zoom>
  );
};

export default KPICard; 